import { memo, useCallback, type FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import { WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type { WhatsAppTemplate, WhatsAppTemplateStatus } from "../WhatsAppCampaignTypes";
import { formatTemplateCategory, formatTemplateLanguage } from "../WhatsAppCampaignUtils";
import { TemplateStatusBadge, WhatsAppMessageText } from "./WhatsAppMessageParts";

const CARD_KEY = `${WHATSAPP_EDITOR_KEY}.card`;

type NoticeTone = "warning" | "info" | "danger" | "muted";

const NOTICE_TONES: Record<WhatsAppTemplateStatus, NoticeTone | null> = {
  approved: null,
  pending: "warning",
  rejected: "danger",
  draft: "muted",
  unavailable: "info",
};

const ACTION_KEYS: Record<WhatsAppTemplateStatus, string> = {
  approved: "useTemplate",
  pending: "viewDetails",
  rejected: "fixAndResubmit",
  draft: "continueEditing",
  unavailable: "recreateTemplate",
};

interface WhatsAppTemplateCardProps {
  template: WhatsAppTemplate;
  onUse: (_template: WhatsAppTemplate) => void;
  onEdit?: (_template: WhatsAppTemplate) => void;
}

const WhatsAppTemplateCard: FC<WhatsAppTemplateCardProps> = ({ template, onUse, onEdit }) => {
  const { t, i18n } = useTranslation();
  const {
    name,
    status,
    body,
    canUse,
    usedInCampaign,
    rejectionReason,
    language,
    category,
    unsupportedReasons,
  } = template;
  const isPreviouslyUsed = status === "unavailable" && Boolean(usedInCampaign);
  const isApproved = status === "approved";
  const noticeTone = NOTICE_TONES[status];
  const actionLabel = t(`${CARD_KEY}.${ACTION_KEYS[status]}`);

  const handleAction = useCallback(
    () => (isApproved ? onUse(template) : onEdit?.(template)),
    [isApproved, onEdit, onUse, template]
  );

  const detailParts = [
    template.variableKeys.length > 0 &&
      t(`${CARD_KEY}.variables`, {
        count: template.variableKeys.length,
        formattedCount: formatNumber(template.variableKeys.length, {}, i18n.language),
      }),
    template.mediaType && t(`${CARD_KEY}.header.${template.mediaType}`),
    template.buttons.length > 0 &&
      t(`${CARD_KEY}.buttons`, {
        count: template.buttons.length,
        formattedCount: formatNumber(template.buttons.length, {}, i18n.language),
      }),
  ].filter(Boolean);

  const renderNotice = () => {
    switch (status) {
      case "pending":
        return t(`${CARD_KEY}.pendingNotice`);
      case "rejected":
        return rejectionReason ? t(`${CARD_KEY}.rejectedNotice`, { reason: rejectionReason }) : null;
      case "draft":
        return t(`${CARD_KEY}.draftNotice`);
      case "unavailable":
        return usedInCampaign ? (
          <Trans
            i18nKey={`${CARD_KEY}.unavailableNotice`}
            values={{ campaign: usedInCampaign }}
            components={{ b: <strong /> }}
          />
        ) : (
          t(`${CARD_KEY}.unavailableGenericNotice`)
        );
      default:
        return null;
    }
  };

  const notice = renderNotice();
  const hasNotice = Boolean(notice && noticeTone) || (isApproved && unsupportedReasons.length > 0);

  return (
    <article className={`wa-template-card ${hasNotice ? "" : "wa-template-card--roomy"}`}>
      <header className="wa-template-card__header">
        <div className="wa-template-card__heading">
          <h4 className="wa-template-card__title">{name}</h4>
          <span className="wa-template-card__subtitle">
            {[formatTemplateCategory(category), formatTemplateLanguage(language, i18n.language)]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
        <TemplateStatusBadge
          status={status}
          label={isPreviouslyUsed ? t(`${WHATSAPP_EDITOR_KEY}.status.previouslyUsed`) : undefined}
        />
      </header>

      <div className="wa-template-card__preview">
        <div className="wa-template-card__bubble" title={body}>
          {body && <WhatsAppMessageText body={body} className="wa-template-card__body" />}
        </div>
      </div>

      {detailParts.length > 0 && (
        <p className="wa-template-card__details">{detailParts.join(" · ")}</p>
      )}

      {isApproved &&
        unsupportedReasons.map(reason => (
          <p key={reason} className="wa-template-card__notice wa-template-card__notice--danger">
            {reason}
          </p>
        ))}

      {notice && noticeTone && (
        <p
          className={`wa-template-card__notice wa-template-card__notice--${noticeTone}`}
          title={typeof notice === "string" ? notice : undefined}
        >
          {notice}
        </p>
      )}

      {(isApproved || onEdit) && (
      <button
        type="button"
        className={`button wa-template-card__action ${
          isApproved ? "button--primary" : "button--outline"
        }`}
        onClick={handleAction}
        disabled={isApproved && !canUse}
        aria-label={t(`${CARD_KEY}.actionLabel`, { action: actionLabel, name })}
      >
        {actionLabel}
      </button>
      )}
    </article>
  );
};

export default memo(WhatsAppTemplateCard);
